import React, { useEffect, useState } from "react";
import { Ticket, Plus, Calendar, Tag, X, Check, AlertCircle, Pause, Play, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * PromoCodesCard — admin UI for the £1-a-month (£12 annual prepay) promo
 * and any future one-off promotion. Lives in the admin dashboard.
 *
 * Default form payload is pre-filled with the founder's brief:
 *   code:       POUND-A-MONTH
 *   £12 annual prepay → £1.99/mo from month 13
 *   live 1 July → 8 July (1 week)
 *
 * Stripe hookup is intentionally a separate step — this card creates the
 * record but the actual checkout swap is gated until the founder pastes in
 * the Stripe `price_…` and `coupon_…` IDs (see /api/admin/promo-codes PATCH).
 */
export default function PromoCodesCard() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // Pre-filled defaults from the founder's brief — adjust in the form
  const DEFAULTS = {
    code: "POUND-A-MONTH",
    label: "£1/month launch promo (annual prepay)",
    mode: "annual_then_monthly",
    annual_amount_pence: 1200,
    annual_label: "£12 for the first year (£1/mo)",
    post_year_monthly_pence: 199,
    post_year_label: "Then £1.99/mo, cancel anytime",
    valid_from: "2026-07-01T00:00:00Z",
    valid_until: "2026-07-08T23:59:59Z",
    max_redemptions: 500,
    active: true,
  };
  const [form, setForm] = useState(DEFAULTS);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promo-codes");
      setCodes(data.promo_codes || []);
    } catch (err) {
      toast.error("Could not load promo codes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/promo-codes", form);
      toast.success(`Promo code ${form.code} created`);
      setShowCreate(false);
      setForm(DEFAULTS);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not create code";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (code_id, currentlyActive) => {
    try {
      await api.patch(`/admin/promo-codes/${code_id}`, { active: !currentlyActive });
      toast.success(currentlyActive ? "Promo paused" : "Promo resumed");
      load();
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (code_id, code) => {
    if (!window.confirm(`Delete promo "${code}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${code_id}`);
      toast.success("Promo deleted");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not delete";
      toast.error(msg);
    }
  };

  return (
    <section className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white" data-testid="admin-promo-codes-card">
      <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <Ticket className="w-3 h-3" /> Promo codes
          </div>
          <h2 className="font-display text-xl sm:text-2xl tracking-tight">£1-a-month launch promo &amp; one-offs</h2>
          <p className="text-xs text-[#666] mt-1 max-w-xl">
            Create &amp; manage time-boxed promo codes. Stripe price IDs need to be wired
            separately before checkout uses these — see backend{" "}
            <code className="bg-[#fff4ee] px-1 rounded">routers/promo_codes.py</code>{" "}
            <code>_apply_promo_to_checkout()</code>.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#ff4500] hover:bg-[#e63e00] text-white text-xs font-bold uppercase tracking-wider"
          data-testid="promo-create-toggle"
        >
          {showCreate ? <><X className="w-3 h-3" /> Cancel</> : <><Plus className="w-3 h-3" /> New promo</>}
        </button>
      </header>

      {showCreate && (
        <form onSubmit={create} className="bg-[#fafafa] border hairline rounded-xl p-4 mb-5 space-y-3" data-testid="promo-create-form">
          <Row label="Code (uppercase, A-Z 0-9 -)">
            <input type="text" required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="input" data-testid="promo-field-code" />
          </Row>
          <Row label="Label (admin only)">
            <input type="text" required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input" />
          </Row>
          <div className="grid sm:grid-cols-2 gap-3">
            <Row label="Year 1 prepay (pence)">
              <input type="number" min="1" required value={form.annual_amount_pence} onChange={(e) => setForm({ ...form, annual_amount_pence: parseInt(e.target.value || "0", 10) })} className="input" />
            </Row>
            <Row label="Year 1 display label">
              <input type="text" required value={form.annual_label} onChange={(e) => setForm({ ...form, annual_label: e.target.value })} className="input" />
            </Row>
            <Row label="Post-year monthly (pence)">
              <input type="number" min="1" required value={form.post_year_monthly_pence} onChange={(e) => setForm({ ...form, post_year_monthly_pence: parseInt(e.target.value || "0", 10) })} className="input" />
            </Row>
            <Row label="Post-year display label">
              <input type="text" required value={form.post_year_label} onChange={(e) => setForm({ ...form, post_year_label: e.target.value })} className="input" />
            </Row>
            <Row label="Valid from (ISO UTC)">
              <input type="datetime-local" required value={(form.valid_from || "").slice(0, 16)} onChange={(e) => setForm({ ...form, valid_from: e.target.value + ":00Z" })} className="input" />
            </Row>
            <Row label="Valid until (ISO UTC)">
              <input type="datetime-local" required value={(form.valid_until || "").slice(0, 16)} onChange={(e) => setForm({ ...form, valid_until: e.target.value + ":00Z" })} className="input" />
            </Row>
            <Row label="Max redemptions (blank = unlimited)">
              <input type="number" min="1" value={form.max_redemptions || ""} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value ? parseInt(e.target.value, 10) : null })} className="input" />
            </Row>
            <Row label="Active immediately?">
              <select value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })} className="input">
                <option value="1">Yes — live</option>
                <option value="0">No — paused</option>
              </select>
            </Row>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={() => { setShowCreate(false); setForm(DEFAULTS); }} className="px-3 py-2 text-xs mono uppercase tracking-wider text-[#666]">Cancel</button>
            <button type="submit" disabled={creating} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#ff4500] hover:bg-[#e63e00] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider" data-testid="promo-create-submit">
              {creating ? "Saving…" : <><Check className="w-3 h-3" /> Create promo</>}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-[#666] mono uppercase tracking-wider py-4">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="text-center py-8 px-4 text-[#666] text-sm">
          <Ticket className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No promo codes yet. Click <strong>New promo</strong> to create your first.
        </div>
      ) : (
        <div className="space-y-2" data-testid="promo-list">
          {codes.map((c) => (
            <PromoRow key={c.code_id} c={c} onToggle={toggleActive} onRemove={remove} />
          ))}
        </div>
      )}

      <style>{`.input { width:100%; padding:.5rem .75rem; font-size:.85rem; border:1px solid #e5e5e5; border-radius:.5rem; background:#fff; } .input:focus { outline:none; border-color:#ff4500; box-shadow:0 0 0 3px rgba(255,69,0,.12); }`}</style>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="mono text-[10px] uppercase tracking-wider text-[#666] mb-1">{label}</div>
      {children}
    </label>
  );
}

function PromoRow({ c, onToggle, onRemove }) {
  const _isStripeWired = !!(c.stripe_price_id_annual);
  const validFrom = c.valid_from ? new Date(c.valid_from) : null;
  const validUntil = c.valid_until ? new Date(c.valid_until) : null;
  return (
    <div className="border hairline rounded-xl p-3 flex items-start justify-between gap-3" data-testid={`promo-row-${c.code}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="mono text-sm font-bold tracking-wider">{c.code}</span>
          {c.active ? (
            <span className="mono text-[9px] uppercase tracking-wider bg-[#ecfdf5] text-[#065f46] px-1.5 py-0.5 rounded-full font-bold">Active</span>
          ) : (
            <span className="mono text-[9px] uppercase tracking-wider bg-[#f5f5f5] text-[#666] px-1.5 py-0.5 rounded-full font-bold">Paused</span>
          )}
          {!_isStripeWired && (
            <span className="mono text-[9px] uppercase tracking-wider bg-[#fef3c7] text-[#92400e] px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-1">
              <AlertCircle className="w-2.5 h-2.5" /> Stripe not wired
            </span>
          )}
        </div>
        <div className="text-sm">{c.label}</div>
        <div className="mono text-[11px] text-[#666] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> {c.annual_label} → {c.post_year_label}</span>
          {validFrom && validUntil && (
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {validFrom.toLocaleDateString("en-GB")} → {validUntil.toLocaleDateString("en-GB")}</span>
          )}
          <span>{c.redemptions_count || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""} redeemed</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <button
          onClick={() => onToggle(c.code_id, c.active)}
          className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md border border-[#ddd] hover:border-[#ff4500] hover:text-[#ff4500] text-[10px] mono uppercase tracking-wider"
          data-testid={`promo-toggle-${c.code}`}
        >
          {c.active ? <><Pause className="w-3 h-3" /> Pause</> : <><Play className="w-3 h-3" /> Resume</>}
        </button>
        <button
          onClick={() => onRemove(c.code_id, c.code)}
          disabled={(c.redemptions_count || 0) > 0}
          className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md border border-[#ddd] hover:border-red-500 hover:text-red-500 text-[10px] mono uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`promo-delete-${c.code}`}
          title={(c.redemptions_count || 0) > 0 ? "Cannot delete after redemptions — pause instead" : "Delete"}
        >
          <Trash2 className="w-3 h-3" /> Delete
        </button>
      </div>
    </div>
  );
}
